/**
 * Formulator constants (epic PI-SPEC-SW-E282, contract formulator 1;
 * formulator 3, epic PI-SPEC-SW-E321, adds the Claude draft, the draft and
 * redraft turn purposes and finding paths; formulator 4, epic
 * PI-SPEC-SW-E322, adds the optional-offer skip quick pick).
 *
 * Single source of truth for the limits the SpecTree API, the SpecTree
 * Toolbox formulator engine, the internal plugin skill and the web pages
 * share. Import these; never re-type the numbers.
 */
/**
 * The marker the internal skill prints at the start of its one result line:
 * `SPECTREE_FORMULATOR_TURN {json}`. The JSON parses with
 * `formulatorTurnMarkerSchema`; the engine posts it (minus `sessionId`) to
 * `POST /api/v1/work-items/interview/:sessionId/reply`.
 */
export const FORMULATOR_TURN_MARKER = "SPECTREE_FORMULATOR_TURN";
/** Version of the `turn.json` file the engine writes for the skill. */
export const FORMULATOR_TURN_INPUT_SCHEMA_VERSION = 1;
/** Longest description a person may type when starting a new item. */
export const FORMULATOR_DESCRIPTION_MAX = 4000;
/** Longest reply accepted in one turn (same as the API's MAX_REPLY_LENGTH). */
export const FORMULATOR_REPLY_MAX = 8000;
/** Longest value Claude may extract for one slot part. */
export const FORMULATOR_EXTRACTED_VALUE_MAX = 4000;
/** Most slot-part keys one engine turn may carry. */
export const FORMULATOR_EXTRACTED_KEYS_MAX = 20;
/** Longest slot-part key name. */
export const FORMULATOR_EXTRACTED_KEY_MAX = 64;
/** Longest question Claude may ask in one turn. */
export const FORMULATOR_QUESTION_MAX = 1000;
/** Most quick-pick answers offered with one question. */
export const FORMULATOR_QUICK_PICKS_MAX = 6;
/** Longest quick-pick answer. */
export const FORMULATOR_QUICK_PICK_MAX = 120;
/**
 * The quick pick that skips the one optional-slot offer (contract formulator
 * 4, ER-1709): once the required slots are filled, the interview offers the
 * optional slots once, and choosing this answer closes the offer without
 * filling them.
 */
export const FORMULATOR_OPTIONAL_SKIP_PICK = "Nothing to add";
/** Most "What Claude found" entries a session keeps. */
export const FORMULATOR_FINDINGS_MAX = 12;
/** Longest finding name (a screen, setting or behaviour, in product words). */
export const FORMULATOR_FINDING_NAME_MAX = 120;
/** Longest explanation of why a finding matters. */
export const FORMULATOR_FINDING_WHY_MAX = 300;
/** Most duplicate candidates the opening turn of a create session may name. */
export const FORMULATOR_DUPLICATES_MAX = 5;
/** Longest duplicate candidate title (the ADO title limit). */
export const FORMULATOR_DUPLICATE_TITLE_MAX = 255;
/** Longest reason a duplicate candidate was named. */
export const FORMULATOR_DUPLICATE_REASON_MAX = 200;
/** Most product areas one session may say the change touches. */
export const FORMULATOR_AREAS_MAX = 12;
/** Longest product-area name. */
export const FORMULATOR_AREA_NAME_MAX = 120;
/** Most transcript turns the engine hands the skill in `turn.json`. */
export const FORMULATOR_TRANSCRIPT_MAX = 12;
/** Longest draft text field: a story description, a bug's expected or actual result, system info. */
export const FORMULATOR_DRAFT_TEXT_MAX = 4000;
/** Most entries in one draft list (acceptance criteria, repro steps). */
export const FORMULATOR_DRAFT_ITEMS_MAX = 20;
/** Longest entry in a draft list, and the longest bug frequency. */
export const FORMULATOR_DRAFT_ITEM_MAX = 500;
/** Longest "Areas affected" note on a draft (what the code showed; a hint, not a requirement). */
export const FORMULATOR_AREAS_NOTE_MAX = 1200;
/** Longest instruction a person may give with Redraft. */
export const FORMULATOR_REDRAFT_INSTRUCTION_MAX = 500;
/** Most file paths one finding may name. */
export const FORMULATOR_FINDING_PATHS_MAX = 10;
/** Longest file path on a finding (repository-relative). */
export const FORMULATOR_FINDING_PATH_MAX = 300;
/**
 * What one `turn.json` asks the skill for (contract formulator 3):
 * `interview` (the next question; the default when `purpose` is absent),
 * `draft` (write the final item text from the complete answers) or
 * `redraft` (rewrite the current draft following the person's instruction).
 */
export const FORMULATOR_TURN_PURPOSES = ["interview", "draft", "redraft"];
/** Who wrote the stored draft: Claude (a draft or redraft turn) or the person (an edit on the review screen). */
export const FORMULATOR_DRAFT_SOURCES = ["claude", "person"];
/**
 * Phases of one interview turn, in order, as the engine reports them in
 * `turn-progress` events: preparing the repository copies, reading the code,
 * checking open items for duplicates (opening turn of a create session only),
 * and thinking about the next question.
 */
export const FORMULATOR_RUN_PHASES = ["preparing-repos", "reading-code", "checking-duplicates", "thinking"];
//# sourceMappingURL=constants.js.map